import React from 'react';
import './ErrorBoundary.css';
import { trackEvent } from '../utils/analytics';
import { reportError } from '../utils/errorReporter';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true, errorId: crypto.randomUUID().slice(0, 8) };
  }

  componentDidCatch(error, errorInfo) {
    reportError(error, {
      source: 'ErrorBoundary',
      componentStack: errorInfo?.componentStack?.split('\n').slice(0, 10).join('\n'),
    });
    trackEvent('error_boundary', {
      metadata: {
        message: error?.message,
        stack: error?.stack?.slice(0, 500),
        componentStack: errorInfo?.componentStack?.slice(0, 500),
        url: window.location.href,
      },
    });
  }

  componentDidMount() {
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  handleGlobalError = (event) => {
    trackEvent('uncaught_error', {
      metadata: {
        message: event.error?.message || event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        url: window.location.href,
      },
    });
  };

  handleUnhandledRejection = (event) => {
    trackEvent('unhandled_rejection', {
      metadata: {
        reason: String(event.reason).slice(0, 500),
        url: window.location.href,
      },
    });
  };

  handleReload = () => {
    this.setState({ hasError: false, errorId: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary-container" role="alert">
          <h1 className="error-boundary-title">
            Something went wrong
          </h1>
          <p className="error-boundary-message">
            Something broke on our end. Try refreshing the page.
          </p>
          {this.state.errorId && (
            <p className="error-boundary-id">
              Error ID: {this.state.errorId}
            </p>
          )}
          <button onClick={this.handleReload} className="error-boundary-button">
            Go to Home
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
